import {
  FormControlLabel,
  FormControlLabelProps,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react";
import React from "react";
import {
  useOptOutOfCampaignStore,
  useOptOutScreenDetailsStore,
} from "../../stores/Hooks";
import {
  EMPTY_CHARACTER,
  getRadioButtonStyleProps,
  PRIVACY_POLICY_LINK,
} from "@pulse/shared-components";
import {
  TextAreaInputField,
  useFoundationColorTokens,
  useRadioButtonColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { OptOutReasonEnum } from "../../stores/OptOutOfCampaignStore";
import { Trans, useTranslation } from "react-i18next";

export const EmailCampaignOptOutForm: React.FC = observer(() => {
  const { t } = useTranslation();
  const tokens = useFoundationColorTokens();
  const radioButtonTokens = useRadioButtonColorTokens();
  const spacing = useSpacing();
  const typography = useTypography();

  const optOutScreenDetailsStore = useOptOutScreenDetailsStore();
  const optOutOfCampaignStore = useOptOutOfCampaignStore();

  const getRadioButtonLabel = (reason: OptOutReasonEnum): string => {
    switch (reason) {
      case OptOutReasonEnum.EmailsNotCorrectlyRendered:
        return t("emailCycles.emailNotCorrectlyRendered");
      case OptOutReasonEnum.NotInterested:
        return t("emailCycles.notInterested");
      case OptOutReasonEnum.NotRightPerson:
        return t("emailCycles.notRightPerson");
      case OptOutReasonEnum.Other:
        return t("emailCycles.other");
      case OptOutReasonEnum.PrivacyConcerns:
        return t("emailCycles.privacyConcerns");
      case OptOutReasonEnum.TooFrequent:
        return t("emailCycles.tooFrequent");
      default:
        return reason satisfies never;
    }
  };

  const radioProps = getRadioButtonStyleProps(
    tokens,
    radioButtonTokens,
    spacing,
    spacing.spaceXS,
  );
  const radioButtonsFormControlLabelProps: FormControlLabelProps[] =
    Object.values(OptOutReasonEnum).map((optOutReason) => ({
      control: <Radio {...radioProps} />,
      label: getRadioButtonLabel(optOutReason),
      value: optOutReason,
    }));

  return (
    <Stack padding={spacing.spaceLG} gap={spacing.spaceLG}>
      {/* Description */}
      <Typography color={tokens.label} {...typography.b1}>
        <Trans
          i18nKey="emailCycles.optOutFormDescription"
          values={{
            email: optOutScreenDetailsStore.email.value,
          }}
          components={{
            span: <span style={typography.s2} />,
          }}
        />
      </Typography>

      {/* Selection options */}
      <Stack>
        <RadioGroup
          value={optOutOfCampaignStore.reasonEnum}
          onChange={(_, value) => optOutOfCampaignStore.setReason(value)}
        >
          {radioButtonsFormControlLabelProps.map(
            (formControlLabelProps, index) => (
              <FormControlLabel key={index} {...formControlLabelProps} />
            ),
          )}
        </RadioGroup>
        <Stack paddingLeft={spacing.space2XL}>
          <TextAreaInputField
            name="otherReason"
            value={optOutOfCampaignStore.description ?? undefined}
            onTextChange={optOutOfCampaignStore.setDescription}
            placeholder={t("emailCycles.otherReasonPlaceholder")}
            label={EMPTY_CHARACTER}
            disabled={
              optOutOfCampaignStore.reasonEnum !== OptOutReasonEnum.Other
            }
          />
        </Stack>
      </Stack>

      {/* Privacy policy link */}
      <Link href={PRIVACY_POLICY_LINK} underline="hover" width="fit-content">
        {t("welcomePage.privacyPolicy")}
      </Link>
    </Stack>
  );
});
